<template>
  <section
    class="hpc-linear"
    :class="[
      schema.settings.backgroundColor,
      `hpc-linear--block-count-${schema.blocks.length}`
    ]"
    :style="{
      'padding-top': `${schema.settings.marginTop}px`,
      'padding-bottom': `${schema.settings.marginBottom}px`
    }"
  >
    <div class="hpc-linear__content" :class="[schema.settings.backgroundColor]">
      <div class="container">
        <div class="hpc-linear__header-bar hpc-linear__side-by-side-container">
          <h4
            class="hpc-linear__header"
            :class="[schema.settings.headerColor]"
          >{{ schema.settings.sectionHeader }}</h4>
          <div class="hpc-linear__blurb-container" v-if="schema.settings.sectionBlurb">
            <p class="hpc-linear__blurb" :class="[schema.settings.blurbColor]">{{ schema.settings.sectionBlurb }}</p>
          </div>
        </div>
      </div>
      <div
        class="hpc-linear__blocks hpc-linear__blocks--static container"
        v-show="showStaticBlocks"
      >
        <a
          class="hpc-linear__block"
          v-if="block.settings.linear_image"
          :href="block.settings.linear_link"
          v-for="block in schema.blocks"
        >
          <figure class="hpc-linear__block-content">
            <div class="hpc-linear__block-image-wrapper">
              <img
                :src="imageSize(block.settings.linear_image, 'x662')"
                :srcset="srcset(block.settings.linear_image)"
                alt
              />
            </div>
            <v-link
              secondary
              class="hpc-linear__collection-name h6"
              :class="[schema.settings.tileTextColor]"
              v-if="$mq !== 'mobile'"
            >{{ block.settings.linear_collection_name }}</v-link>
            <p
              class="hpc-linear__collection-name-mobile body-md text-center"
              :class="[schema.settings.tileTextColor]"
              v-else
            >{{ block.settings.linear_collection_name }}</p>
            <p
              v-if="block.settings.linear_collection_description"
              class="hpc-linear__block-description body-md"
              :class="[schema.settings.tileTextColor]"
            >{{ block.settings.linear_collection_description }}</p>
          </figure>
        </a>
      </div>
      <div
        class="hpc-linear__blocks hpc-linear__blocks--swiper container"
        v-show="!showStaticBlocks"
      >
        <swiper :options="swiperOptions">
          <div class="hpc-linear__pagination" slot="pagination"></div>
          <swiper-slide
            v-for="block in schema.blocks"
            :key="block.settings.linear_link + block.settings.linear_image"
          >
            <a
              class="hpc-linear__block"
              v-if="block.settings.linear_image"
              :href="block.settings.linear_link"
            >
              <figure class="hpc-linear__block-content">
                <div class="hpc-linear__block-image-wrapper">
                  <img
                    :src="imageSize(block.settings.linear_image, 'x662')"
                    :srcset="srcset(block.settings.linear_image)"
                    alt
                  />
                </div>
                <v-link
                  secondary
                  class="hpc-linear__collection-name h6"
                  :class="[schema.settings.tileTextColor]"
                  v-if="$mq !== 'mobile'"
                >{{ block.settings.linear_collection_name }}</v-link>
                <p
                  class="hpc-linear__collection-name-mobile body-md text-center"
                  :class="[schema.settings.tileTextColor]"
                  v-else
                >{{ block.settings.linear_collection_name }}</p>
                <p
                  v-if="block.settings.linear_collection_description"
                  class="hpc-linear__block-description body-md"
                  :class="[schema.settings.tileTextColor]"
                >{{ block.settings.linear_collection_description }}</p>
              </figure>
            </a>
          </swiper-slide>
        </swiper>
      </div>
    </div>
  </section>
</template>

<script>
import { imageSize, imageCrop } from '../../helpers/util';
import { swiper, swiperSlide } from 'vue-awesome-swiper';
import VLink from '@/scripts/components/buttons/VLink.vue';

export default {
  name: 'CollectionLayoutLinear',
  components: {
    swiper,
    swiperSlide,
    VLink,
  },
  props: {
    schema: Object,
  },
  data() {
    return {
      swiperOptions: {
        pagination: { el: '.hpc-linear__pagination', clickable: true },
        spaceBetween: 8,
        slidesPerView: 2,
        breakpoints: {
          374: {
            slidesPerView: 1,
          },
        },
      },
    };
  },
  computed: {
    showStaticBlocks() {
      return this.$mq !== 'mobile';
    },
  },
  methods: {
    srcset(image) {
      return [
        `${imageSize(image, 'x993')} 1.5x`,
        `${imageSize(image, 'x1324')} 2x`,
      ].join(', ');
    },
    imageSize,
    imageCrop,
  },
};
</script>

<style lang="scss">
.hpc-linear {
  display: flex;
  justify-content: center;

  &__block-description {
    @include mobile-only {
      display: none;
    }
  }

  &__side-by-side-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 28px;

    @include mobile-only {
      flex-direction: column;
      margin-bottom: 42px;
    }
  }

  &__header {
    @include mobile-only {
      margin-bottom: 8px;
    }
  }

  &__blurb {
    max-width: 233px;
    @include mobile-only {
      margin: 0;
      max-width: none;
    }
  }

  &__collection-name {
    &.h6 {
      @include h6-desktop;
      font-family: $font-family-primary;
      margin-bottom: 4px;

      @include mobile-tablet {
        @include h6-mobile;
      }
    }
  }

  &__collection-name-mobile {
    font-weight: 500;
    font-size: rem(13);
    line-height: 20px;
  }

  &__content {
    width: 100%;
  }

  &__side-by-side-container * {
    vertical-align: middle;
  }

  &__blocks {
    position: relative;
    font-size: 0;
    text-align: left;
    display: flex;
    align-items: flex-start;
    max-width: 1440px;
    margin: 0 auto;
    overflow: hidden;
  }

  &__block {
    display: block;
    flex: 1;

    &:hover figcaption,
    &:focus figcaption {
      // todo
      transform: translateY(-2px);
    }

    @include tablet-up {
      margin: 0 6px;
    }
  }

  &__block * {
    width: 100%;
  }

  &__block-image-wrapper {
    position: relative;
    display: flex;
    width: 100%;
    height: 0;
    margin-bottom: 8px;
    padding-bottom: 125%;

    img {
      position: absolute;
      top: 0;
      left: 0;
      z-index: 1;
      width: 100%;
      height: 100%;
      object-fit: cover;
      border: 1px solid transparent;
      transition: border 150ms ease-in-out;
      &:hover {
       border-color: $color-black;
      }
    }
  }

  &__pagination {
    display: block;
    padding: 20px 0;
    text-align: center;

    .swiper-pagination-bullet {
      margin-top: 16px !important;
    }
  }

  figure {
    display: inline-block;
    width: 100%;
  }

  figure img {
    margin-bottom: 18px;
    max-width: 100%;
  }

  figcaption {
    transition: transform 0.1s ease-in-out;
    white-space: pre-line;
  }

  .swiper-pagination-bullet {
    width: $collection-linear-pagination-size;
    height: $collection-linear-pagination-size;
    background: transparent;
    margin: 0 2px !important;
    border: 1px solid $collection-linear-pagination-color--active;
  }

  .swiper-pagination-bullet-active {
    background: $collection-linear-pagination-color--active;
  }

  @include tablet-up {
    figure img {
      margin-bottom: 24px;
    }
  }
}
</style>
